import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import IFolio from "../../models/IFolio";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import { useContextLeftSidebarData } from '../../contexts/refreshDataSource';
import useEnum from '../../hooks/useEnum';
import { MOIS } from './enum';
import { useContextPopupPage } from '../../components/popups/PopupPage';

interface IGestionFoliosProps {
  isModeSelection?: boolean,
}

export default function GestionFolios({ isModeSelection }: IGestionFoliosProps) {
  const { displayedElementInactif, setDisplayedElementInactif } = useContextLeftSidebarData();
  const clientFormDataSource = useCustomDataSource<IFolio, IFolio>({ path: "folio", primaryKey: "idFolio", customReadByIdPath: "folio/{key}/gestion", includeInactives: displayedElementInactif });
  const { setMainForm, selectedDataRow: selectedFolio, mode, onFieldDataChanged, setMode, isFieldModifier, setIsFieldModifier } = clientFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { editorOptions: typeDeCompteEditorOptions } = useEnum(undefined, "gestionFolioTypeCompte", '50%');
  const { setSelectedValue } = useContextPopupPage();

  const { montantBudgetEditorOptions, noCompteOptions, actifOptions, descriptionOptions } = useMemo(() => {
    return {
      montantBudgetEditorOptions: {
        format: { type: 'currency', precision: 2 },
        width: '20%'
      },
      noCompteOptions: { maxLength: 15 },
      actifOptions: { text: "Actif" },
      descriptionOptions: {
        maxLength: 50,
        width: '43%'
      },
      // typeCompteOptions: {
      //   maxLength: 2,
      // },
    };
  }, []);

  // Cet useEffect permet de renitialiser la valueur boolean du hook useContextLeftSidebarData pour rafraichir les donnees
  useEffect(() => {
    setDisplayedElementInactif(false)
    setMainForm(formRef.current?.instance());
  }, [])

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("noCompte")?.focus();
  }, [mode])

  return (
    <FormListBase data={clientFormDataSource}>
      <FormListBaseGrid dataSource={clientFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier) setMode(Mode.Lecture);
          if (setSelectedValue && e.row?.data) {
            setSelectedValue(e.row.data);
          }
        }}
      >
        <Column key={"grid-col-no-compte"} dataField={"noCompte"} caption="Numéro de compte" allowHiding={false} />
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-type-compte"} dataField={"typeCompte"} caption="Type de compte" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedFolio?.description || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture ?
        <FormListBaseTab>
          <TabItem title="Informations">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedFolio || {} as IFolio} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem>
                <GroupItem colCount={3}>
                  <SimpleItem key={"frm-no-compte"} dataField={"noCompte"}
                    editorOptions={
                      noCompteOptions
                    }>
                    <Label text={"No. compte"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                    editorOptions={
                      actifOptions
                    }
                  >
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <SimpleItem key={"frm-description"} dataField={"description"}
                  editorOptions={
                    descriptionOptions
                  } />
                <SimpleItem key={"frm-type-compte"} dataField={"typeCompte"} editorType={"dxSelectBox"} editorOptions={typeDeCompteEditorOptions}>
                  <Label text={"Type de compte"} />
                </SimpleItem>
                <SimpleItem
                  key={"frm-montant-budget"}
                  dataField={"montantBudget"}
                  editorType={"dxNumberBox"}
                  editorOptions={montantBudgetEditorOptions}>
                  <Label text={"Montant budget"} />
                </SimpleItem>
                <GroupItem caption={"Revenus par période"}>
                  <DataGrid
                    showBorders={true}
                    allowColumnResizing={true}
                    dataSource={[
                      ...MOIS.map(mois => ({
                        mois: mois,
                        montant1: 0,
                        montant2: 0,
                        montant3: 0
                      })),
                      {
                        //  mois: "Total",
                        montant1: 0,
                        montant2: 0,
                        montant3: 0
                      }
                    ]}
                  >
                    <Column caption={"Période"} dataField={"mois"} />
                    <Column caption={`${new Date().getFullYear()}/${new Date().getFullYear() + 1}`} dataField={"montant1"} dataType="number"
                      format={{
                        formatter: (value: number | Date) => {
                          if (typeof value === 'number') {
                            return value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' $' || '0,00 $';
                          }
                          return '0,00 $';
                        }
                      }}
                    />
                    <Column caption={`${new Date().getFullYear() - 1}/${new Date().getFullYear()}`} dataField={"montant2"} dataType="number"
                      format={{
                        formatter: (value: number | Date) => {
                          if (typeof value === 'number') {
                            return value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' $' || '0,00 $';
                          }
                          return '0,00 $';
                        }
                      }}
                    />
                    <Column caption={`${new Date().getFullYear() - 2}/${new Date().getFullYear() - 1}`} dataField={"montant3"} dataType="number"
                      format={{
                        formatter: (value: number | Date) => {
                          if (typeof value === 'number') {
                            return value?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' $' || '0,00 $';
                          }
                          return '0,00 $';
                        }
                      }}
                    />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedFolio || {} as IFolio} /></TabItem>
        </FormListBaseTab> : null}
    </FormListBase>
  );
}
